import { useDispatch, useSelector } from "react-redux";
import {
  IonAvatar,
  IonCard,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonNote,
  IonSearchbar,
  IonText,
  isPlatform,
  IonCol,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  useIonViewWillEnter,
  useIonViewWillLeave,
  IonLoading,
} from "@ionic/react";
import { selectDocuments } from "../../../store/supervisorHome/selectors/selectDocuments";
import {
  documentTextOutline,
  personOutline,
  calendarOutline,
  bookmarkOutline,
} from "ionicons/icons";
import { DocumentStatusTag } from "../../../components/Document/DocumentStatusTag";
import { setDocumentSearchText, setMaxListIndex } from "../../../store/supervisorHome";
import { selectDocumentsFetchStatus } from "../../../store/supervisorHome/selectors/selectDocumentsFetchStatus";
import { selectDocumentsSearchText } from "../../../store/supervisorHome/selectors/selectDocumentsSearchText";
import { DocumentShare } from "../../../components/Document/DocumentShare";
import { DocumentShareMobile } from "../../../components/Document/DocumentShareMobile";
import { useEffect, useState } from "react";
import { RootState } from "../../../store/rootReducer";

const MIN_ITEM_ON_LIST = 10;

export const TodoListSection: React.FC = () => {
  const dispatch = useDispatch();
  const documents = useSelector(selectDocuments);
  const searchText = useSelector(selectDocumentsSearchText);
  const fetchStatus = useSelector(selectDocumentsFetchStatus);
  const maxDataList = useSelector((state: RootState) => state.supervisorHome.maxListIndex);

  const [infiniteDisabled, setInfiniteDisabled] = useState<boolean>(false);

  // Normaliza la lista por si el reducer guardó [list, count] en vez de un array plano
  const visibleDocs = Array.isArray(documents) && Array.isArray((documents as any)[0])
    ? (documents as any)[0]
    : (Array.isArray(documents) ? documents : []);

  useEffect(() => {
    dispatch(setMaxListIndex(MIN_ITEM_ON_LIST));
    setInfiniteDisabled(false);
  }, [visibleDocs]);

  const pushData = () => {
    dispatch(setMaxListIndex(maxDataList + MIN_ITEM_ON_LIST));
  };

  const loadData = (ev: any) => {
    setTimeout(() => {
      pushData();
      ev.target.complete();
      if (maxDataList > visibleDocs.length) {
        setInfiniteDisabled(true);
      }
    }, 500);
  };

  useIonViewWillEnter(() => {
    dispatch(setMaxListIndex(MIN_ITEM_ON_LIST));
  });

  useIonViewWillLeave(() => {
    dispatch(setMaxListIndex(MIN_ITEM_ON_LIST));
  });

  const convertDigitIn = (document: any) => {
    const d2 = document.createdAt.split("/");
    const startDate: string = d2[2] + "-" + d2[1] + "-" + d2[0];
    const date1: Date = new Date();
    const date2: Date = new Date(startDate);
    const timeInMilisec: number = date1.getTime() - date2.getTime();
    const daysBetweenDates: number = Math.ceil(timeInMilisec / (1000 * 60 * 60 * 24));

    if (isNaN(daysBetweenDates)) {
      return "Error en la fecha ingresada";
    }
    if (daysBetweenDates < 0) {
      return "Error en el formato de fecha ingresado";
    }
    if (daysBetweenDates === 0) {
      return "Ingresado hoy";
    }
    if (daysBetweenDates > 0) {
      return "Hace " + daysBetweenDates + " días";
    }
  };

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('TodoListSection visibleDocs sample:', visibleDocs[0], 'count=', visibleDocs.length);
  }

  return (
    <>
      <h2 className="ion-padding-horizontal">
          <IonText color="primary">Acuerdos en curso ({visibleDocs.length})</IonText>
      </h2>
      <IonSearchbar
        style={{ maxWidth: 360 }}
        placeholder="Buscar acuerdo"
        value={searchText}
        onIonChange={({ detail: { value = "" } }) => dispatch(setDocumentSearchText(value))}
        class="ion-no-padding ion-padding-start ion-padding-bottom"
      ></IonSearchbar>
      {/* {fetchStatus === "pending" && <p>Cargando...</p>} */}
      <IonLoading isOpen={fetchStatus === "pending"} message={"Cargando..."} />
      {fetchStatus === "fulfilled" && (
        <IonGrid>
          {visibleDocs.length === 0 && (
            <IonCard>
              <IonItem lines="none">
                <IonLabel>
                  <IonText color="medium">No se han encontrado acuerdos</IonText>
                </IonLabel>
              </IonItem>
            </IonCard>
          )}

          {visibleDocs.slice(0, maxDataList).map((document: any) => (

            <IonCard key={document.id} class="ion-margin-bottom">
              <IonItem routerLink={`/app/supervision/acuerdos/${document.id}`} lines="none">
                {isPlatform("desktop") && (
                  <IonAvatar slot="start">
                    <IonIcon icon={documentTextOutline} size="large" />
                  </IonAvatar>
                )}
                <IonLabel>
                  <h2 style={{ fontWeight: 500 }} className="ion-text-wrap">
                    {document.title}
                  </h2>
                  <h4>
                    <IonIcon icon={personOutline}></IonIcon>
                    {document.nextSigner?.name}
                  </h4>
                  <p>
                    <IonIcon icon={calendarOutline}></IonIcon>
                    <IonText class="ion-padding-start">{convertDigitIn(document)}</IonText>
                    <IonIcon icon={bookmarkOutline} class="ion-padding-start" />
                    <IonText class="ion-padding-start">{`${document.category?.name}`}</IonText>
                  </p>
                </IonLabel>

                <IonNote slot="end">
                  <DocumentStatusTag document={document} />
                </IonNote>
              </IonItem>
              <DocumentShare document={document} />
              <DocumentShareMobile document={document} />
            </IonCard>
          ))}
          <IonInfiniteScroll onIonInfinite={loadData} threshold="10px" disabled={infiniteDisabled}>
            <IonInfiniteScrollContent loadingSpinner="bubbles" loadingText="Cargar más datos..." />
          </IonInfiniteScroll>
        </IonGrid>
      )}
    </>
  );
};
